import styled from 'styled-components';
import { UploadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import {
	Card,
	Divider,
	Button,
	Modal,
	Form,
	Input,
	message,
	Select,
	Upload,
} from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import {
	getbulkSenderList,
	createBulkSender,
	createInstantFund,
} from '../../methods/redux/reducers/bulkSender';
import Bulk from './bulk';
import { getCurrencies } from '../../methods/redux/reducers/currency';

export const TableHeader = styled.th`
	padding: 12px 20px;
	text-align: left;
	font-weight: 800;
	color: #262626;
	border-bottom: 1px solid #f0f0f0;
	background-color: #f8f8f8;
	margin-bottom: 10px;
	display: grid;
	justify-content: space-between;
	grid-template-columns: 1fr 1fr 1fr;
`;

export const StyledFormItem = styled(Form.Item)`
	.ant-row {
		display: block;
	}
`;

const BulkSender = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isInstantModalVisible, setIsInstantModalVisible] = useState(false);
	const [refresh, setRefresh] = useState(0);
	const [form] = Form.useForm();

	const { walletSnapshotReport } = useAppSelector((store) => store.reports);
	const { bulkSenderList } = useAppSelector((store) => store.bulkSender);
	const { currencies } = useAppSelector((store) => store.currencies);

	const [isLoadingCreateJob, setIsLoadingCreateJob] = useState(false);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(walletSnapshotReport.paginator.perPage),
			});
		} else {
			dispatch(getbulkSenderList(queryData));
		}

		dispatch(getCurrencies({}));
	}, [searchParams, dispatch, walletSnapshotReport.paginator.perPage, refresh]);

	// const updateQueryString = (query: any, action = 'update') => {
	// 	remakeQueryString(query, searchParams, setSearchParams, action);
	// };

	const currencySelect = currencies.map((item: any) => ({
		label: item.symbol,
		value: item.symbol,
		id: item._id,
	}));

	const currencyIdSelect = currencies.map((item: any) => ({
		label: item.symbol,
		value: item._id,
	}));

	const showModal = () => {
		setIsModalVisible(true);
	};

	const handleCancel = () => {
		setIsModalVisible(false);
		form.resetFields();
	};

	const showInstantModal = () => {
		setIsInstantModalVisible(true);
	};

	const handleInstantModalCancel = () => {
		setIsInstantModalVisible(false);
		form.resetFields();
	};

	const handleSubmit = async (values: any) => {
		try {
			setIsLoadingCreateJob(true);

			const formData = new FormData();
			formData.append('funder', values.funderEmail);
			formData.append('title', values.title);
			formData.append('currency', values.currency);
			if (values.userList) {
				formData.append('csv_file', values.userList.file);
			}

			const jobData = values.moneyBook
				? {
						title: values.title,
						funder: values.funderEmail,
						currency: values.currency,
						money_book: values.moneyBook,
				  }
				: {
						title: values.title,
						funder: values.funderEmail,
						currency: values.currency,
				  };

			await dispatch(createBulkSender({ params: jobData, payload: formData }));
			setRefresh(Math.random());
			handleCancel();
			form.resetFields();
		} catch (error) {
			message.error('Failed to create bulk sender job');
		} finally {
			setIsLoadingCreateJob(false);
		}
	};

	const handleInstantPayment = async (values: any) => {
		try {
			setIsLoadingCreateJob(true);

			const jobData = {
				senderEmail: values.funderEmail,
				receiverEmail: values.receiverEmail,
				amount: values.amount,
				currencyId: values.currency,
				title: values.description,
			};

			await dispatch(createInstantFund(jobData));
			handleInstantModalCancel();
			form.resetFields();
		} catch (error) {
			message.error('Failed to create instant payment job');
		} finally {
			setIsLoadingCreateJob(false);
		}
	};

	return (
		<Card>
			<h1>Bulk Fund Sender Jobs</h1>
			<p>List of bulk fund sending jobs and their current status.</p>

			<div
				style={{
					display: 'flex',
					gap: '16px',
					marginTop: '16px',
					marginBottom: '24px',
				}}
			>
				<Button type="primary" onClick={showModal}>
					Create Job
				</Button>

				<Button
					type="default"
					onClick={() => {
						showInstantModal();
					}}
				>
					Pay Instant
				</Button>
			</div>

			<Divider />

			<p>
				Total of <strong>{bulkSenderList?.jobs?.length} items</strong> found
			</p>

			<Divider />

			{/* <Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={walletSnapshotReport.paginator.itemCount}
				current={walletSnapshotReport.paginator.currentPage}
				pageSize={walletSnapshotReport.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/> */}

			<br />

			<TableHeader>
				<div>Title</div>
				<div>Funder</div>
				<div>Status</div>
			</TableHeader>

			{bulkSenderList?.jobs?.map((item: any) => (
				<Bulk key={item._id} item={item} />
			))}

			<br />

			<Divider />
			{/* <Pagination
				onChange={(pageId) => updateQueryString({ pageId })}
				total={walletSnapshotReport.paginator.itemCount}
				current={walletSnapshotReport.paginator.currentPage}
				pageSize={walletSnapshotReport.paginator.perPage}
				showSizeChanger
				showQuickJumper
			/> */}

			{/* Modal for Creating a Job */}
			<Modal
				title="Create Job"
				visible={isModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleSubmit}>
					<StyledFormItem
						name="title"
						label="Job Title"
						rules={[{ required: true, message: 'Please input the job title!' }]}
					>
						<Input placeholder="e.g., January Salary from Xend" />
					</StyledFormItem>
					<StyledFormItem
						name="funderEmail"
						label="Funder Email"
						rules={[
							{ required: true, message: 'Please input the funder email!' },
						]}
						initialValue="salary_service@xend.finance"
					>
						<Input placeholder="Enter funder email" />
					</StyledFormItem>

					<StyledFormItem
						name="currency"
						label="Currency to Xend"
						rules={[{ required: true, message: 'Please select a currency!' }]}
						initialValue="USDT"
					>
						<Select placeholder="Select a currency" options={currencySelect} />
					</StyledFormItem>
					<StyledFormItem
						name="userList"
						label="User List * (non-users will be omitted)"
						style={{ marginBottom: '0px' }}
						// rules={[{ required: true, message: 'Please upload a CSV file!' }]}
					>
						<Upload beforeUpload={() => false} accept=".csv">
							<Button icon={<UploadOutlined />}>Choose file</Button>
						</Upload>
					</StyledFormItem>
					<p
						style={{
							textAlign: 'center',
							margin: ' 2px 0px',
						}}
					>
						OR
					</p>
					<div>
						<StyledFormItem
							name="moneyBook"
							style={{ marginBottom: '0px' }}
							label="Select Money Book"
						>
							<Select placeholder="-- Select money book --">
								{bulkSenderList?.moneyBook.map((book: any) => (
									<Select.Option key={book?._id} value={book?._id}>
										{book?.title}
									</Select.Option>
								))}
							</Select>
						</StyledFormItem>

						<p style={{ marginBottom: '24px', fontSize: '12px' }}>
							<a
								href={`${process.env.REACT_APP_BASEURI}/static/front/assets/files/sample_bulk_sender.csv`}
								style={{ color: 'purple' }}
							>
								See sample CSV file (A=email, B=amount)
							</a>
						</p>
					</div>
					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={isLoadingCreateJob}
						>
							{isLoadingCreateJob ? 'Creating...' : 'Create Job'}
						</Button>
						<Button onClick={handleCancel} style={{ marginLeft: '8px' }}>
							Cancel
						</Button>
					</Form.Item>
				</Form>
			</Modal>

			{/* Modal for Instant Payment */}
			<Modal
				title="Instant Payment"
				visible={isInstantModalVisible}
				onCancel={handleInstantModalCancel}
				footer={null}
			>
				<Form form={form} onFinish={handleInstantPayment}>
					<StyledFormItem
						name="funderEmail"
						label="Funder Email"
						rules={[
							{ required: true, message: 'Please input the funder email!' },
						]}
						initialValue="salary_service@xend.finance"
					>
						<Input placeholder="Enter funder email" />
					</StyledFormItem>

					<StyledFormItem
						name="receiverEmail"
						label="Receiver Email"
						rules={[
							{ required: true, message: 'Please input the receiver email!' },
						]}
					>
						<Input placeholder="Enter receiver email" />
					</StyledFormItem>

					<StyledFormItem
						name="currency"
						label="Currency"
						rules={[{ required: true, message: 'Please select a currency!' }]}
					>
						<Select
							placeholder="Select a currency"
							options={currencyIdSelect}
						/>
					</StyledFormItem>

					<StyledFormItem
						name="amount"
						label="Amount"
						rules={[{ required: true, message: 'Please input the amount' }]}
					>
						<Input placeholder="e.g., Amount" />
					</StyledFormItem>

					<StyledFormItem
						name="description"
						label="Description"
						rules={[
							{ required: true, message: 'Please input the description' },
						]}
					>
						<Input placeholder="e.g., January Salary from Xend" />
					</StyledFormItem>

					<Form.Item>
						<Button
							type="primary"
							htmlType="submit"
							loading={isLoadingCreateJob}
						>
							{isLoadingCreateJob ? 'Distributing...' : 'Pay Now'}
						</Button>
					</Form.Item>

					<Divider />
					<p>
						Sample message to user: You have received a total of 200.00 USDT
						from : <strong>Salary for may</strong>
					</p>
				</Form>
			</Modal>
		</Card>
	);
};

export default BulkSender;
